import { useCallback, useEffect, useState } from "react";
import type { DeviceCommandType, DeviceSummary } from "@kidtube/shared";
import { api } from "../api";
import type { AppPage } from "../components/AppShell";
import { EmptyState } from "../components/Feedback";
import { formatDuration, formatMinutes } from "../utils";

interface DashboardScreenProps {
  showSuccess: (message: string) => void;
  showError: (error: unknown) => void;
  onNavigate: (page: AppPage) => void;
}

const COMMAND_LABELS: Readonly<Record<string, string>> = {
  PAUSE: "Đã gửi lệnh tạm dừng.",
  RESUME: "Đã gửi lệnh tiếp tục.",
  STOP: "Đã gửi lệnh dừng video.",
  LOCK: "Đã gửi lệnh khóa KidTube.",
  UNLOCK: "Đã gửi lệnh mở khóa KidTube.",
  REFRESH: "Đã yêu cầu thiết bị làm mới nội dung.",
};

export function DashboardScreen({ showSuccess, showError, onNavigate }: DashboardScreenProps) {
  const [devices, setDevices] = useState<DeviceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        setDevices(await api.devices());
      } catch (error) {
        if (!silent) showError(error);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [showError],
  );

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(true), 5_000);
    return () => window.clearInterval(timer);
  }, [load]);

  async function sendCommand(
    device: DeviceSummary,
    type: Exclude<DeviceCommandType, "BLOCK_AND_STOP">,
  ) {
    setActing(`${device.id}:${type}`);
    try {
      await api.command(device.id, type);
      showSuccess(COMMAND_LABELS[type] ?? "Đã gửi lệnh đến thiết bị.");
      window.setTimeout(() => void load(true), 900);
    } catch (error) {
      showError(error);
    } finally {
      setActing(null);
    }
  }

  async function blockVideo(device: DeviceSummary) {
    const videoId = device.playback?.videoId;
    if (videoId === null || videoId === undefined) return;
    setActing(`${device.id}:BLOCK_VIDEO`);
    try {
      await api.blockVideo(videoId, "Blocked from current playback");
      showSuccess("Đã chặn video trên mọi thiết bị KidTube.");
      await load(true);
    } catch (error) {
      showError(error);
    } finally {
      setActing(null);
    }
  }

  async function blockChannel(device: DeviceSummary) {
    const playback = device.playback;
    if (playback?.channelId === null || playback?.channelId === undefined) return;
    setActing(`${device.id}:BLOCK_CHANNEL`);
    try {
      await api.blockChannel(
        playback.channelId,
        playback.channelName ?? "",
        "Blocked from current playback",
      );
      showSuccess("Đã chặn kênh trên mọi thiết bị KidTube.");
      await load(true);
    } catch (error) {
      showError(error);
    } finally {
      setActing(null);
    }
  }

  async function blockAndStop(device: DeviceSummary) {
    const videoId = device.playback?.videoId;
    if (videoId === null || videoId === undefined) return;
    setActing(`${device.id}:BLOCK_AND_STOP`);
    try {
      await api.blockAndStop(device.id, videoId);
      showSuccess("Đã chặn video và gửi lệnh dừng ngay.");
      window.setTimeout(() => void load(true), 900);
    } catch (error) {
      showError(error);
    } finally {
      setActing(null);
    }
  }

  const onlineCount = devices.filter((device) => device.online).length;
  const playingCount = devices.filter((device) => device.playback?.state === "PLAYING").length;
  const tvCount = devices.filter((device) => device.deviceKind === "TV").length;
  const webChildCount = devices.filter((device) => device.deviceKind === "WEB_CHILD").length;
  const canPair = tvCount < 2 || webChildCount < 1;

  return (
    <div className="page-stack">
      <header className="page-header dashboard-header">
        <div>
          <p className="eyebrow">KIDTUBE PARENT</p>
          <h1>Tổng quan gia đình</h1>
          <p>Theo dõi hai TV và một Child Web trên iPhone gần như theo thời gian thực.</p>
        </div>
        <button type="button" className="secondary-button" onClick={() => void load()} disabled={loading}>
          ↻ Làm mới
        </button>
      </header>

      <section className="summary-grid" aria-label="Trạng thái KidTube">
        <article className="summary-card accent-blue">
          <span className="summary-icon" aria-hidden="true">▣</span>
          <div><strong>{tvCount}/2 · {webChildCount}/1</strong><small>TV · Child Web</small></div>
        </article>
        <article className="summary-card accent-green">
          <span className="summary-icon" aria-hidden="true">●</span>
          <div><strong>{onlineCount}</strong><small>Thiết bị online</small></div>
        </article>
        <article className="summary-card accent-violet">
          <span className="summary-icon" aria-hidden="true">▶</span>
          <div><strong>{playingCount}</strong><small>Đang phát video</small></div>
        </article>
      </section>

      <section className="section-block">
        <div className="section-heading">
          <div><h2>Thiết bị của gia đình</h2><p>Cập nhật tự động mỗi 5 giây.</p></div>
          {canPair && (
            <button type="button" className="primary-button" onClick={() => onNavigate("settings")}>
              + Liên kết thiết bị
            </button>
          )}
        </div>

        {loading && devices.length === 0 ? (
          <div className="device-grid" aria-label="Đang tải thiết bị">
            <div className="skeleton skeleton-device" />
            <div className="skeleton skeleton-device" />
          </div>
        ) : devices.length === 0 ? (
          <EmptyState
            icon="▣"
            title="Chưa có thiết bị KidTube"
            description="Mở KidTube trên TV hoặc Child Web để lấy mã 6 số, sau đó nhập mã tại trang Thiết bị."
            action={{ label: "Liên kết thiết bị", onClick: () => onNavigate("settings") }}
          />
        ) : (
          <div className="device-grid">
            {devices.map((device) => {
              const playback = device.playback;
              const commandBusy = acting?.startsWith(`${device.id}:`) === true;
              return (
                <article className="device-card" key={device.id}>
                  <header className="device-card-header">
                    <div className={`device-platform ${device.deviceKind === "WEB_CHILD" ? "web" : device.platform === "SAMSUNG_TIZEN" ? "samsung" : "android"}`}>
                      {device.deviceKind === "WEB_CHILD" ? "W" : device.platform === "SAMSUNG_TIZEN" ? "TV" : "A"}
                    </div>
                    <div className="device-name">
                      <h3>{device.name}</h3>
                      <p>{device.model} · {device.osVersion}</p>
                    </div>
                    <span className={`status-pill ${device.online ? "online" : "offline"}`}>
                      {device.online ? "Online" : "Offline"}
                    </span>
                  </header>

                  <div className="playback-panel">
                    {playback?.videoId !== null && playback?.videoId !== undefined ? (
                      <>
                        <img
                          src={playback.thumbnailUrl ?? "/assets/video-placeholder.png"}
                          alt=""
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                        <div className="playback-copy">
                          <span className={`play-state ${playback.state.toLocaleLowerCase("en")}`}>
                            {playback.state === "PLAYING" ? "▶ Đang phát" : playback.state === "PAUSED" ? "Ⅱ Tạm dừng" : playback.state}
                          </span>
                          <h4>{playback.title ?? "Video KidTube"}</h4>
                          <p>{playback.channelName ?? "—"}</p>
                          <div className="progress-track" aria-label={`Tiến độ ${formatDuration(playback.positionSeconds)} trên ${formatDuration(playback.durationSeconds)}`}>
                            <span style={{ width: `${Math.min(100, playback.durationSeconds > 0 ? (playback.positionSeconds / playback.durationSeconds) * 100 : 0)}%` }} />
                          </div>
                          <small>{formatDuration(playback.positionSeconds)} / {formatDuration(playback.durationSeconds)}</small>
                        </div>
                      </>
                    ) : (
                      <div className="device-idle">
                        <span aria-hidden="true">⌂</span>
                        <div><strong>Đang ở màn hình KidTube</strong><small>Chưa phát video</small></div>
                      </div>
                    )}
                  </div>

                  <div className="usage-line">
                    <span>Hôm nay: <strong>{formatMinutes(device.usedTodaySeconds / 60)}</strong></span>
                    <span>Giới hạn: <strong>{device.dailyLimitMinutes === null ? "Chưa đặt" : formatMinutes(device.dailyLimitMinutes)}</strong></span>
                  </div>

                  <div className="command-grid">
                    {playback?.state === "PLAYING" ? (
                      <button type="button" onClick={() => void sendCommand(device, "PAUSE")} disabled={!device.online || commandBusy}>Ⅱ Tạm dừng</button>
                    ) : (
                      <button type="button" onClick={() => void sendCommand(device, "RESUME")} disabled={!device.online || commandBusy || playback?.videoId == null}>▶ Tiếp tục</button>
                    )}
                    <button type="button" onClick={() => void sendCommand(device, "STOP")} disabled={!device.online || commandBusy || playback?.videoId == null}>■ Dừng</button>
                    <button type="button" onClick={() => void sendCommand(device, device.locked ? "UNLOCK" : "LOCK")} disabled={!device.online || commandBusy}>
                      {device.locked ? "⌾ Mở khóa" : "⌾ Khóa"}
                    </button>
                    <button type="button" onClick={() => void sendCommand(device, "REFRESH")} disabled={!device.online || commandBusy}>↻ Làm mới</button>
                  </div>

                  {playback?.videoId != null && (
                    <div className="danger-actions">
                      <button type="button" onClick={() => void blockVideo(device)} disabled={commandBusy}>Chặn video</button>
                      <button type="button" onClick={() => void blockChannel(device)} disabled={commandBusy || playback.channelId == null}>Chặn kênh</button>
                      <button type="button" className="danger-solid" onClick={() => void blockAndStop(device)} disabled={!device.online || commandBusy}>Chặn &amp; dừng ngay</button>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
